import React, { useState, useEffect, useMemo, useRef } from 'react';
import GoogleMap, { DirectionsRenderer } from '@/components/GoogleMap';
import FTLStopHelpers from '../../utilities/FTLStopHelpers';
import { colors } from '@/styles';
import { DropOff, PickUp, StartLocation, EndLocation, Truck } from '../DispatchPlan/map/MapMarkers';
import { useDriverLocation } from '../../hooks';
import { useClientUser } from '../../hooks';

import { useShapeFieldTypeOptions, useFieldShapesSingle, getStopShape } from '../DispatchPlan/hooks/shapes';
import { stopHasVIPOrder } from '../DispatchPlan/map/vipMarker';

import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { renderShapeFieldTypeMenuItems } from '../GoogleMap/controls/ShapeFieldTypeMenuItems';

import { LastUpdatedControl } from '../GoogleMap/controls/LastUpdatedControl';
import routeStatusOptions from '../../constants/routeStatusOptions';
import { ROUTE_CUSTOM_MAP_MARKERS, SHOW_REC_EQUIPMENT_SHAPE_TAGS } from '@/constants/featureFlags';

const DEFAULT_CENTER = { lat: 39.74, lng: -104.99 };
const DEFAULT_ZOOM = 13;
const NOW = new Date().getTime();
const ONE_DAY = 86400000;

function RouteMap(props) {
    const { stops, orders = [], driverId, routeKey, routeStatus, deliveryDate } = props;

    const { savedShapes, user_id, shipping_partners, circles } = useClientUser();
    const mapRef = useRef();
    const [directions, setDirections] = useState(null);
    const [route, setRoute] = useState(null);
    const [stopSequence, setStopSequence] = useState(null);
    const [shapeFieldType, setShapeFieldType] = useState('order_tags');
    const [lockShapeType, setLockShapeType] = useState(false);

    // Shape feature flags
    const showRecEquipmentShapeTags = circles?.[SHOW_REC_EQUIPMENT_SHAPE_TAGS];
    const routeCustomShapeMapMarkersEnabled = circles?.[ROUTE_CUSTOM_MAP_MARKERS];

    const shapeFieldTypeOptions = useShapeFieldTypeOptions(orders, user_id, showRecEquipmentShapeTags);

    useEffect(() => {
        if (!lockShapeType) {
            setShapeFieldType(shapeFieldTypeOptions[0]);
        }
    }, [shapeFieldTypeOptions]);

    const fieldShapesSingle = useFieldShapesSingle({
        orders,
        savedShapes,
        shapeFieldType,
        shipping_partners,
        user_id,
        includeRecommendedEquipment: showRecEquipmentShapeTags,
    });

    const { location } = useDriverLocation(routeKey, {
        disabled:
            !routeKey ||
            !(deliveryDate && Math.abs(new Date(deliveryDate).getTime() - NOW) < ONE_DAY) ||
            routeStatus !== routeStatusOptions.INPROGRESS.value,
    });

    const ordersById = useMemo(() => {
        return Object.fromEntries(orders.map((order) => [order.order_id, order]));
    }, [orders]);

    const mostRecentStopLocation = useMemo(() => {
        if (!stops?.length) return null;

        const mostRecentStop = stops.reduce((latest, stop) => {
            if (!stop.arrival_time || !latest.arrival_time) {
                return stop.arrival_time ? stop : latest;
            }
            return new Date(stop.arrival_time) > new Date(latest.arrival_time) ? stop : latest;
        }, stops[0]);

        if (!mostRecentStop?.lat || !mostRecentStop?.lng) return null;

        return {
            lat: mostRecentStop.lat,
            lng: mostRecentStop.lng,
        };
    }, [stops]);

    const bounds = useMemo(() => {
        if (!stopSequence?.length) {
            return;
        }

        return stopSequence.map((stop) => ({
            lat: stop.lat,
            lng: stop.lng,
        }));
    }, [stopSequence]);

    useEffect(() => {
        getDirections();
    }, [stops]);

    const getDirections = async () => {
        if (!stops) return;

        const stopsWithoutLunch = stops.filter((s) => s.type !== 'LUNCH');

        const _stopSequence = FTLStopHelpers.getStopSequence(stopsWithoutLunch);
        if (!_stopSequence.length) return;

        if (!_stopSequence.every((_stop) => _stop.lat && _stop.lng)) {
            await FTLStopHelpers.addLatLngToStops(_stopSequence);
        }

        setStopSequence(_stopSequence);

        try {
            const directionsResult = await FTLStopHelpers.getGMapsDirections(_stopSequence);
            if (directionsResult.status === 'OK') {
                setDirections(directionsResult);
                const _route = directionsResult.routes[0];
                setRoute(_route);
            } else {
                console.error(directionsResult.status);
                // dispatch(
                //     notificationActions.enqueueSnackbar(
                //         getErrorSnackbar('There was an error computing directions for this route.', {}, dispatch)
                //     )
                // );
            }
        } catch (error) {
            console.error(error);
            // dispatch(
            //     notificationActions.enqueueSnackbar(
            //         getErrorSnackbar('There was an error computing directions for this route.', {}, dispatch)
            //     )
            // );
        }
    };

    const renderStopMarker = (stop) => {
        const color = stop.stop_completion_time ? colors.greens.primary : colors.reds.primary;
        const hasVIP = stopHasVIPOrder(stop, ordersById);

        if (stop.start) {
            return <StartLocation key="start-marker" position={{ lat: stop.lat, lng: stop.lng }} color={color} />;
        } else if (stop.end) {
            return <EndLocation key="end-marker" position={{ lat: stop.lat, lng: stop.lng }} color={color} />;
        } else if (stop.type === 'DROPOFF' || (stop.type === 'PICKUP' && stop.returns?.length)) {
            if (routeCustomShapeMapMarkersEnabled) {
                const shape = getStopShape(
                    stop,
                    ordersById,
                    shapeFieldType,
                    fieldShapesSingle,
                    showRecEquipmentShapeTags
                );
                return (
                    <DropOff
                        key={`dropoff-marker-${stop.ordering}`}
                        iconLabel={stop.ordering}
                        stopNumber={stop.ordering}
                        position={{ lat: stop.lat, lng: stop.lng }}
                        color={color}
                        shape={shape}
                        hasVIP={hasVIP}
                    />
                );
            }
            return (
                <DropOff
                    key={`dropoff-marker-${stop.ordering}`}
                    iconLabel={stop.ordering}
                    stopNumber={stop.ordering}
                    position={{ lat: stop.lat, lng: stop.lng }}
                    color={color}
                    hasVIP={hasVIP}
                />
            );
        } else if (stop.type === 'PICKUP' && !stop.returns?.length) {
            return (
                <PickUp
                    key={`pickup-marker-${stop.ordering}`}
                    position={{ lat: stop.lat, lng: stop.lng }}
                    color={color}
                />
            );
        }
    };

    const renderStopMarkers = (stops = []) => {
        const stopAddresses = stops.reduce((acc, stop) => [...acc, stop.address], []);

        return stops.map((stop) => {
            const addresses = stopAddresses.filter((stopAddress) => stopAddress === stop.address);

            if (addresses.length > 1) {
                const min = 0.99999;
                const max = 1.00001;
                const offsetLat = stop.lat * (Math.random() * (max - min) + min);
                const offsetLng = stop.lng * (Math.random() * (max - min) + min);

                stop.lat = offsetLat;
                stop.lng = offsetLng;
            }

            return renderStopMarker(stop);
        });
    };

    return (
        <div style={{ position: 'relative', height: '100%', width: '100%' }}>
            {routeCustomShapeMapMarkersEnabled && (
                <FormControl
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        zIndex: 1,
                        backgroundColor: 'white',
                        minWidth: 160,
                    }}
                >
                    <Select
                        value={shapeFieldType}
                        onChange={(e) => {
                            setShapeFieldType(e.target.value);
                            setLockShapeType(true);
                        }}
                    >
                        {renderShapeFieldTypeMenuItems(shapeFieldTypeOptions)}
                    </Select>
                </FormControl>
            )}
            <GoogleMap
                ref={mapRef}
                bounds={bounds}
                center={!bounds || bounds.length === 0 ? DEFAULT_CENTER : undefined}
                zoom={!bounds || bounds.length === 0 ? DEFAULT_ZOOM : undefined}
                mapTypeControl={false}
                fullscreenControl={false}
                zoomControl={false}
                streetViewControl={false}
            >
                {directions && route && (
                    <DirectionsRenderer directions={directions} options={{ suppressMarkers: true }} />
                )}
                {directions && stopSequence && <>{renderStopMarkers(stopSequence)}</>}
                {((location?.lat && location?.lng) || (mostRecentStopLocation?.lat && mostRecentStopLocation?.lng)) && (
                    <Truck
                        key={'truck-marker'}
                        position={{
                            lat: location?.lat || mostRecentStopLocation?.lat,
                            lng: location?.lng || mostRecentStopLocation?.lng,
                        }}
                        // css rotation direction is flipped, and truck icon without rotation is at 90 degrees
                        rotation={90 - location.direction}
                    />
                )}
            </GoogleMap>
            {driverId && routeStatus === routeStatusOptions.INPROGRESS.value && (
                <LastUpdatedControl mapRef={mapRef} lastUpdated={location?.lastUpdated} onRefresh={() => {}} />
            )}
        </div>
    );
}

export default React.memo(RouteMap);
